import {
  Input,
  Row,
  Col,
  Upload,
  GetProp,
  UploadProps,
  UploadFile,
  message,
  notification,
  Flex,
  Button,
  Modal,
} from "antd";
import { PlusOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { useEditProductContext } from "../../../contexts/edit-product-context/useEditProductContext";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { Image as ImageIcon } from "@phosphor-icons/react";
import { useParams } from "react-router-dom";
import Cookies from "js-cookie";
import { useMutation, useQueryClient } from "react-query";
import { removeImage } from "../../../service";
import { MediaEditor } from "../../molecules/media-editor/MediaEditor";
import { colors } from "../../../styles/theme";
import { PriceInput } from "../../atoms/PriceInput";
import { useBranchContext } from "../../../contexts/branch-context";
import { v4 as uuidv4 } from "uuid";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

const accessToken = Cookies.get("accessToken");

type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];

const getBase64 = (file: FileType): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

const UploadWrapper = styled.div`
  width: 100%;
  max-width: 250px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;

  @media (max-width: 1200px) {
    max-width: 200px;
  }

  @media (max-width: 768px) {
    max-width: 100%;
    width: 200px;
    margin-bottom: 16px;
  }
`;

const StyledUpload = styled(Upload)`
  width: 100%;

  .ant-upload-list-item,
  .ant-upload {
    width: 100% !important;
    aspect-ratio: 1 / 1 !important;
    height: auto !important;
  }

  .ant-upload-list-item-container {
    width: 100% !important;
    aspect-ratio: 1 / 1 !important;
    height: auto !important;
  }

  .ant-upload-list-item-image {
    object-fit: cover !important;
  }

  .ant-upload.ant-upload-select {
    width: 100% !important;
    aspect-ratio: 1 / 1 !important;
    height: auto !important;
  }

  .ant-upload-list {
    width: 100%;
  }
`;

const UploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
`;

const UploadText = styled.div`
  font-size: 14px;
  color: var(--color-text-secondary);
  margin-top: 8px;
`;

const validateImageFile = (file: any, t: TFunction): Promise<boolean> => {
  // Check file type
  const isImage = file.type.startsWith("image/");
  if (!isImage) {
    message.error(t("productMainData.onlyImagesAllowed"));
    return Promise.resolve(false);
  }

  // Check file format
  const validFormats = ["image/jpeg", "image/png", "image/webp"];
  if (!validFormats.includes(file.type)) {
    notification.error({
      type: "error",
      message: t("productMainData.invalidFileFormat"),
      description: t("productMainData.allowedFormats"),
      placement: "bottomRight",
    });
    return Promise.resolve(false);
  }

  // Check file size (max 10MB before editing)
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    notification.error({
      type: "error",
      message: t("productMainData.fileTooLarge"),
      description: t("productMainData.maxSize"),
      placement: "bottomRight",
    });
    return Promise.resolve(false);
  }

  // Check minimum resolution
  const img: HTMLImageElement = new window.Image();
  img.src = URL.createObjectURL(file);

  return new Promise((resolve) => {
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      const { width, height } = img;

      if (width < 200 || height < 200) {
        notification.error({
          type: "error",
          message: t("productMainData.resolutionTooSmall"),
          description: t("productMainData.minResolution"),
          placement: "bottomRight",
        });
        resolve(false);
      } else {
        resolve(true);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      message.error(t("productMainData.failedToLoadImage"));
      resolve(false);
    };
  });
};

export const ProductMainData: React.FC = () => {
  const { t } = useTranslation();
  const { currentBranch } = useBranchContext();
  const { productID } = useParams();

  const {
    name,
    setName,
    descripotion,
    setDescription,
    imageUrlS3,
    setUnsavedChanges,
    unsavedChanges,
    productBaseVariants,
    setProductBaseVariants,
  } = useEditProductContext();
  const [, setPreviewOpen] = useState(false);
  const [, setPreviewImage] = useState("");
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [editorVisible, setEditorVisible] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as FileType);
    }

    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
  };

  const handleBeforeUpload = async (file: File) => {
    const isValid = await validateImageFile(file, t);
    if (isValid) {
      setSelectedFile(file);
      setEditorVisible(true);
    }
    return false; // Prevent automatic upload
  };

  const handleEditorConfirm = async (croppedBlob: Blob) => {
    if (!productID) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", croppedBlob, "cropped-image.jpg");

    try {
      const API_PATH = window.location.host === "test-admin.proste.menu"
        ? "https://test-server.proste.menu"
        : (process.env.REACT_APP_API_URL || "http://localhost:8000");

      const response = await fetch(
        `${API_PATH}/v1/products_api/admin/product/${productID}/image`,

        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        }
      );

      if (response.ok) {
        notification.success({
          type: "success",
          message: t("productMainData.newImageUploaded"),
          placement: "bottomRight",
        });

        // Refresh the page data
        query.invalidateQueries([`/product`]);
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      notification.error({
        type: "error",
        message: t("productMainData.imageUploadError"),
        placement: "bottomRight",
      });
    } finally {
      setIsUploading(false);
      setEditorVisible(false);
      setSelectedFile(null);
    }
  };

  const handleEditorCancel = () => {
    setEditorVisible(false);
    setSelectedFile(null);
  };
  const query = useQueryClient();

  const {
    mutateAsync: removeImageMutateAsync,
    isLoading: removeImageMutateAsyncIsLoading,
  } = useMutation(removeImage, {
    onSuccess: (data) => {
      setUnsavedChanges(false);
      query.invalidateQueries([`/product`]);
      notification.success({
        message: t("productMainData.edited"),
        style: {
          background: colors.successBackground,
        },
        placement: "bottomRight",
      });
    },
    onError: () => {
      query.invalidateQueries([`/product`]);
      setUnsavedChanges(false);
      notification.error({
        message: t("productMainData.deleteError"),
        style: {
          background: colors.errorBackground,
        },
        placement: "bottomRight",
      });
    },
  });

  const uploadButton = (
    <Button type="text" style={{ width: "100%", height: "100%", padding: 0 }} block>
      <UploadContainer>
        <ImageIcon size={32} weight="light" />
        <UploadText>{t("productMainData.addProductImage")}</UploadText>
      </UploadContainer>
    </Button>
  );

  useEffect(() => {
    if (imageUrlS3) {
      setPreviewImage(`${imageUrlS3}?id=${new Date().getTime()}`);
      setFileList([
        {
          uid: "-1",
          name: "image.png",
          status: "done",
          url: `${imageUrlS3}?id=${new Date().getTime()}`,
        },
      ]);
    } else {
      // Clear state when image is removed
      setPreviewImage("");
      setFileList([]);
    }
  }, [imageUrlS3]);

  const hasSingleVariant = productBaseVariants.length === 1;

  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <Row gutter={[16, 16]}>
        {/* Na mobile zdjęcie jest pierwsze, na desktop drugie */}
        <Col xs={{ span: 24, order: 1 }} md={{ span: 8, order: 2 }}>
          <UploadWrapper>
            <StyledUpload
              disabled={removeImageMutateAsyncIsLoading || unsavedChanges || isUploading}
              beforeUpload={handleBeforeUpload}
              onRemove={() =>
                removeImageMutateAsync({
                  imageID: imageUrlS3.split("/")[
                    imageUrlS3.split("/").length - 1
                  ],
                  productID,
                })
              }
              listType="picture-card"
              fileList={fileList}
              onPreview={fileList.length >= 1 ? undefined : handlePreview}
              showUploadList={{
                showPreviewIcon: false,
                showRemoveIcon: !isUploading,
              }}
            >
              {fileList.length >= 1 ? null : uploadButton}
            </StyledUpload>
          </UploadWrapper>
          <MediaEditor
            visible={editorVisible}
            file={selectedFile}
            onCancel={handleEditorCancel}
            onConfirm={handleEditorConfirm}
          />
        </Col>

        {/* Na mobile inputy są drugie, na desktop pierwsze */}
        <Col xs={{ span: 24, order: 2 }} md={{ span: 16, order: 1 }}>
          <Flex vertical gap={8}>
            <label style={{ fontSize: "14px", fontWeight: 500 }}>{t("productMainData.nameLabel")}</label>
            <Input
              value={name}
              onChange={(e) => {
                setUnsavedChanges(true);
                setName(e.target.value);
              }}
              placeholder="Placeholder"
            />
          </Flex>
          <Flex vertical gap={8} style={{ marginTop: "20px" }}>
            <label style={{ fontSize: "14px", fontWeight: 500 }}>{t("productMainData.descriptionLabel")}</label>
            <Input.TextArea
              value={descripotion}
              onChange={(e) => {
                setUnsavedChanges(true);
                setDescription(e.target.value);
              }}
              rows={6}
              placeholder="Placeholder"
            />
          </Flex>
          {hasSingleVariant && (
            <Flex vertical gap={8} style={{ marginTop: "20px" }}>
              <label style={{ fontSize: "14px", fontWeight: 500 }}>{t("productMainData.priceLabel")}</label>
              <PriceInput
                value={productBaseVariants[0].price}
                onChange={(price) => {
                  setUnsavedChanges(true);
                  setProductBaseVariants((current: any) => {
                    const newArray = [...current];
                    newArray[0].price = price;
                    return newArray;
                  });
                }}
                editMode={true}
                width="100%"
                currency={currentBranch?.currency}
              />
              <Flex gap={8} align="center" style={{ marginTop: "8px" }}>
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setUnsavedChanges(true);
                    setProductBaseVariants((current: any) => {
                      const newArray = current;
                      newArray.push({
                        id: "NEW_VARIANT-" + uuidv4(),
                        key: uuidv4(),
                        name: "",
                        price: 0,
                      });
                      return [...newArray];
                    });
                  }}
                  style={{ width: "fit-content" }}
                >
                  {t("productMainData.addVariant")}
                </Button>
                <Button
                  type="link"
                  icon={<QuestionCircleOutlined />}
                  onClick={() => setIsModalOpen(true)}
                  style={{ padding: 0, height: "auto" }}
                >
                  {t("productMainData.whatAreVariants")}
                </Button>
              </Flex>
            </Flex>
          )}
        </Col>
      </Row>
      {hasSingleVariant && (
        <Modal
          title={t("productMainData.variantsModalTitle")}
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          footer={[
            <Button key="close" type="primary" onClick={() => setIsModalOpen(false)}>
              {t("productMainData.understood")}
            </Button>,
          ]}
          width={600}
        >
          <div style={{ lineHeight: 1.8 }}>
            <p>
              <strong>{t("productMainData.variantsIntroBold")}</strong> {t("productMainData.variantsIntroText")}
            </p>

            <h4>{t("productMainData.usageExamples")}</h4>

            <div style={{ marginBottom: 16 }}>
              <strong>{t("productMainData.examplePizzaTitle")}</strong>
              <ul>
                <li>{t("productMainData.examplePizzaSmall")}</li>
                <li>{t("productMainData.examplePizzaMedium")}</li>
                <li>{t("productMainData.examplePizzaLarge")}</li>
              </ul>
            </div>

            <div style={{ marginBottom: 16 }}>
              <strong>{t("productMainData.exampleCoffeeTitle")}</strong>
              <ul>
                <li>{t("productMainData.exampleCoffeeEspresso")}</li>
                <li>{t("productMainData.exampleCoffeeDoubleEspresso")}</li>
                <li>{t("productMainData.exampleCoffeeAmericano")}</li>
              </ul>
            </div>

            <div style={{ marginBottom: 16 }}>
              <strong>{t("productMainData.exampleDrinksTitle")}</strong>
              <ul>
                <li>{t("productMainData.exampleDrinksSmall")}</li>
                <li>{t("productMainData.exampleDrinksMedium")}</li>
                <li>{t("productMainData.exampleDrinksLarge")}</li>
              </ul>
            </div>

            <p style={{ marginTop: 16, color: "var(--color-text-secondary)" }}>
              <strong>{t("productMainData.tipLabel")}</strong> {t("productMainData.tipText")}
            </p>
          </div>
        </Modal>
      )}
    </>
  );
};
